import React, { useState, useEffect } from "react";
import { X, Upload, Loader2 } from "lucide-react";
import { AnnouncementFormData, AnnouncementImageState, SystemAnnouncement } from "../../../../types/features/administration/SystemAnnouncement";
import { API_BASE } from "../../../../config/api";

interface AnnouncementFormProps {
  isOpen: boolean;
  editingAnnouncement: SystemAnnouncement | null;
  onSubmit: (data: AnnouncementFormData, imageFile: File | null) => Promise<void>;
  onClose: () => void;
}

const initialFormData: AnnouncementFormData = {
  title: "",
  description: "",
  status: "ACTIVE",
  is_block: false,
};

export const AnnouncementForm: React.FC<AnnouncementFormProps> = ({
  isOpen,
  editingAnnouncement,
  onSubmit,
  onClose,
}) => {
  const [formData, setFormData] = useState<AnnouncementFormData>(initialFormData);
  const [image, setImage] = useState<AnnouncementImageState>({
    file: null,
    preview: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editingAnnouncement) {
      setFormData({
        title: editingAnnouncement.title,
        description: editingAnnouncement.description || "",
        status: editingAnnouncement.status,
        is_block: editingAnnouncement.is_block,
      });
      setImage({
        file: null,
        preview: editingAnnouncement.image ? `${API_BASE}${editingAnnouncement.image}` : null,
      });
    } else {
      setFormData(initialFormData);
      setImage({ file: null, preview: null });
    }
  }, [editingAnnouncement]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage({
          file,
          preview: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData, image.file);
      handleClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setFormData(initialFormData);
    setImage({ file: null, preview: null });
    onClose();
  };

  // Déterminer si on est en mode édition avec image
  const hasExistingImage = editingAnnouncement?.image ? true : false;

  // La description est désactivée si :
  // - On est en mode édition ET l'annonce a déjà une image
  // - Ou si on a sélectionné une nouvelle image
  const isDescriptionDisabled = (hasExistingImage && !formData.description) || !!image.file;

  // L'image est désactivée si on a une description (existante ou nouvelle)
  const isImageDisabled = !!formData.description;

  const isSubmitDisabled = !formData.title.trim() || isSubmitting;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {editingAnnouncement ? "Modifier l'annonce" : "Nouvelle annonce"}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Titre
            </label>
            <input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              disabled={isSubmitting}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description{" "}
              {isDescriptionDisabled && (
                <span className="text-xs text-gray-500">(impossible car l'annonce a déjà une image)</span>
              )}
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              disabled={isDescriptionDisabled || isSubmitting}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">
              {isDescriptionDisabled
                ? "Pour ajouter une description, supprimez d'abord l'image"
                : "Soit une description, soit une image (pas les deux)"}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image{" "}
              {isImageDisabled && (
                <span className="text-xs text-gray-500">(impossible car une description est déjà présente)</span>
              )}
            </label>
            <div className="flex items-center gap-4">
              <label
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  isImageDisabled || isSubmitting
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 cursor-pointer hover:bg-gray-200"
                }`}
              >
                <Upload className="w-5 h-5" />
                {image.preview ? "Changer l'image" : "Choisir une image"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={isImageDisabled || isSubmitting}
                  className="hidden"
                />
              </label>

              {image.file && (
                <>
                  <span className="text-sm text-gray-600">{image.file.name}</span>
                  <button
                    type="button"
                    onClick={() => setImage({ file: null, preview: null })}
                    disabled={isSubmitting}
                    className="text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>

            {image.preview && (
              <img src={image.preview} alt="Preview" className="mt-4 w-full h-48 object-cover rounded-lg" />
            )}

            {hasExistingImage && !image.file && !isImageDisabled && (
              <p className="text-xs text-green-600 mt-2">Image existante. Vous pouvez la conserver ou la remplacer.</p>
            )}
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as "ACTIVE" | "DESACTIVE" })}
              disabled={isSubmitting}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="ACTIVE">Active</option>
              <option value="DESACTIVE">Désactivée</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_block"
              checked={formData.is_block}
              onChange={(e) => setFormData({ ...formData, is_block: e.target.checked })}
              disabled={isSubmitting}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label htmlFor="is_block" className="text-sm font-medium text-gray-700">
              Annonce bloquante (empêche l'utilisation de l'application)
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? "En cours..." : editingAnnouncement ? "Mettre à jour" : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
